import React, { useState, useCallback, useEffect } from 'react'
import Breadcrumb, { BreadcrumbItem } from '@zera-admin/breadcrumb'
import Page, { PageHeader, Container } from '@zera-admin/page'
import Button from '@zera-admin/button'
import { uid } from '@zera-admin/utils'

import { useFlagcardContext } from 'app/contexts/flagcard'
import { usePopupContext } from 'app/contexts/popup'
import MainLayout from 'layouts/main'
import userService, { User } from 'services/http/identity-server/user'

import { UpdateUserProps } from './types'
import UserForm from './components/UserForm'

const UpdateUser: React.FunctionComponent<UpdateUserProps> = ({
  match,
  history,
}) => {
  const [isLoading, setIsLoading] = useState(false)
  const [isReady, setIsReady] = useState(false)
  const [flagcard] = useFlagcardContext()
  const [popup] = usePopupContext()
  const [values, setValues] = useState<User>({
    name: '',
    surname: '',
    phoneNumber: '',
    password: '',
    email: '',
    userName: '',
    roles: [],
    apps: [],
  })

  const handleUpdate = async (data: User) => {
    if (values) {
      setIsLoading(true)

      try {
        await userService.update({ ...data, id: match.params.id })

        flagcard.add({
          title: 'Kullanıcı güncellendi',
          children: 'Geçerli kullanıcı başarıyla güncellendi.',
        })
      } catch {
        popup.add({
          title: 'Kullanıcı güncellenemedi',
          children:
            'Kullanıcı güncellenirken bir sorun oluştu, lütfen tekrar deneyin.',
        })
      }

      setIsLoading(false)
    }
  }

  const handleDelete = () => {
    const popupId = uid()

    const onClick = () => {
      if (values) {
        setIsLoading(true)

        userService
          .del(match.params.id)
          .then(() => {
            flagcard.add({
              title: 'Kullanıcı silindi',
              children: 'Geçerli kullanıcı başarıyla silindi.',
            })

            history.push('/users')
          })
          .catch(() => {
            popup.add({
              title: 'Kullanıcı silinemedi',
              children:
                'Kullanıcı silinirken bir sorun oluştu, lütfen sayfayı yenileyip tekrar deneyin.',
            })
          })
          .finally(() => {
            setIsLoading(false)
            popup.remove(popupId)
          })
      }
    }

    const onClose = () => {
      popup.remove(popupId)
    }

    popup.add({
      id: popupId,
      appearance: 'warning',
      title: 'Kullanıcı silinsin mi?',
      children:
        'Bu kullanıcıyı silmek istediğinize emin misiniz? Bu işlem geri alınamaz.',
      actions: [
        {
          isLoading,
          onClick: onClick,
          text: 'Sil',
        },
        {
          onClick: onClose,
          text: 'Vazgeç',
        },
      ],
      onClose,
    })
  }

  const getValues = useCallback(async () => {
    const id = match.params.id

    try {
      const response = await userService.getById(id)

      setValues(response.data)
    } catch {
      const popupId = uid()

      const onClose = () => {
        history.push('/users')
        popup.remove(popupId)
      }

      popup.add({
        id: popupId,
        title: 'Kullanıcı getirilemedi',
        children:
          'Kullanıcı bilgisi getirilirken bi sorun oluştu, lütfen sayfayı yenileyip tekrar deneyin.',
        onClose,
        actions: [
          {
            appearance: 'primary',
            text: 'Geri dön',
            onClick: onClose,
          },
        ],
      })
    }

    setIsReady(true)
  }, [match.params.id, popup, history])

  useEffect(() => {
    getValues()
  }, [getValues])

  const breadcrumbs = (
    <Breadcrumb>
      <BreadcrumbItem text="Anasayfa" href="/" />
      <BreadcrumbItem text="Kullanıcılar" href="/users" />
      <BreadcrumbItem text="Kullanıcı detayı" />
    </Breadcrumb>
  )

  return (
    <MainLayout isLoading={!isReady}>
      <Container width="narrow">
        <Page>
          <PageHeader
            breadcrumbs={breadcrumbs}
            actions={
              <Button
                appearance="error"
                isDisabled={isLoading}
                onClick={handleDelete}
              >
                Kullanıcıyı sil
              </Button>
            }
            bottomBar={
              <p>
                Oluşturduğunuz kullanıcı bilgilerini aşağıdan
                güncelleyebilirsiniz.
              </p>
            }
          >
            Kullanıcı görüntüleniyor: {values?.userName || '-'}
          </PageHeader>
          <UserForm
            loading={isLoading}
            values={values}
            operation="update"
            onSubmit={handleUpdate}
          />
        </Page>
      </Container>
    </MainLayout>
  )
}

export default UpdateUser
